import React from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '../ui/badge';

interface PlaceholderBadgesProps {
  allowedParams: string[];
  onInsert: (param: string) => void;
  /** Prefix for the `data-testid` of each badge button. */
  testIdPrefix: string;
  /** Stacks the badges and shows each placeholder's description beside it. */
  withDescriptions?: boolean;
}

/**
 * Clickable `{placeholder}` badges that insert the token into the edited
 * message. Presentational — insertion is handled by the containing card.
 */
export const PlaceholderBadges: React.FC<PlaceholderBadgesProps> = ({
  allowedParams,
  onInsert,
  testIdPrefix,
  withDescriptions = false,
}) => {
  const { t } = useTranslation('admin');

  return (
    <div className={withDescriptions ? 'flex flex-col gap-2' : 'flex flex-wrap gap-2'}>
      {allowedParams.map((param) => {
        const button = (
          <button
            key={param}
            type="button"
            onClick={() => onInsert(param)}
            data-testid={`${testIdPrefix}-placeholder-${param}`}
          >
            <Badge variant="outline" className="cursor-pointer font-mono hover:bg-muted">
              {`{${param}}`}
            </Badge>
          </button>
        );

        if (!withDescriptions) {
          return button;
        }

        return (
          <div key={param} className="flex items-center gap-2">
            {button}
            <span className="text-xs text-muted-foreground">
              {t(`notificationTemplates.placeholders.${param}`, { defaultValue: param })}
            </span>
          </div>
        );
      })}
    </div>
  );
};
